import { getAddress } from '@ethersproject/address'
import { BigNumber } from '@ethersproject/bignumber'
import { toOnChainAddress } from '@sirius-cybernetics-42/product-config'
import { getExplorerBaseUrl } from '../config/chainParams'
import { ChainId, JSBI, Percent, CurrencyAmount } from '../sdk'

// returns the checksummed address if the address is valid, otherwise returns false
export function isAddress(value: any): string | false {
  try {
    return getAddress(value)
  } catch {
    return false
  }
}

export function getScanLink(chainId: ChainId, data: string, type: 'transaction' | 'token' | 'address'): string {
  const base = getExplorerBaseUrl(chainId)

  switch (type) {
    case 'transaction': {
      return `${base}/tx/${data}`
    }
    case 'token': {
      return `${base}/token/${data}`
    }
    case 'address':
    default: {
      return `${base}/address/${data}`
    }
  }
}

/** Dirección consultable en el explorador (nativo de catálogo → WETH). */
export function getTokenExplorerAddress(address: string): string {
  return toOnChainAddress(address)
}

// shorten the checksummed version of the input address to have 0x + 4 characters at start and end
export function shortenAddress(address: string, chars = 4): string {
  const parsed = isAddress(address)
  if (!parsed) {
    throw Error(`Invalid 'address' parameter '${address}'.`)
  }
  return `${parsed.substring(0, chars + 2)}...${parsed.substring(42 - chars)}`
}

/** Trunca direcciones para UI; tolera mocks sin checksum EIP-55. */
export function formatAddressDisplay(address: string, chars = 4): string {
  const parsed = isAddress(address)
  if (parsed) {
    return `${parsed.substring(0, chars + 2)}...${parsed.substring(42 - chars)}`
  }

  const normalized = address.trim()
  if (normalized.length <= chars * 2 + 5) return normalized
  if (normalized.startsWith('0x') && normalized.length > 10) {
    return `${normalized.slice(0, chars + 2)}...${normalized.slice(-chars)}`
  }
  return normalized
}

// add 10%
export function calculateGasMargin(value: BigNumber): BigNumber {
  return value.mul(BigNumber.from(10000).add(BigNumber.from(1000))).div(BigNumber.from(10000))
}

// converts a basis points value to a sdk percent
export function basisPointsToPercent(num: number): Percent {
  return new Percent(JSBI.BigInt(num), JSBI.BigInt(10000))
}

export function calculateSlippageAmount(value: CurrencyAmount, slippage: number): [JSBI, JSBI] {
  if (slippage < 0 || slippage > 10000) {
    throw Error(`Unexpected slippage value: ${slippage}`)
  }
  return [
    JSBI.divide(JSBI.multiply(value.raw, JSBI.BigInt(10000 - slippage)), JSBI.BigInt(10000)),
    JSBI.divide(JSBI.multiply(value.raw, JSBI.BigInt(10000 + slippage)), JSBI.BigInt(10000)),
  ]
}

export function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}
